import { __extends } from "tslib";
import { MethodBase, _Shader_MethodBase } from './MethodBase';
/**
 * EffectLightMapMethod provides a method that allows applying a light map texture to the calculated pixel colour.
 * It is different from DiffuseLightMapMethod in that the latter only modulates the diffuse shading value rather
 * than the whole pixel colour.
 */
var EffectLightMapMethod = /** @class */ (function (_super) {
    __extends(EffectLightMapMethod, _super);
    /**
     * Creates a new EffectLightMapMethod object.
     *
     * @param lightMap The texture containing the light map.
     * @param blendMode The blend mode with which the light map should be applied to the lighting result.
     * @param useSecondaryUV Indicates whether the secondary UV set should be used to map the light map.
     */
    function EffectLightMapMethod(lightMap, blendMode, useSecondaryUV) {
        if (blendMode === void 0) { blendMode = 'multiply'; }
        if (useSecondaryUV === void 0) { useSecondaryUV = false; }
        var _this = _super.call(this) || this;
        if (blendMode != EffectLightMapMethod.ADD && blendMode != EffectLightMapMethod.MULTIPLY)
            throw new Error('Unknown blendmode!');
        _this._lightMap = lightMap;
        _this._blendMode = blendMode;
        _this._useSecondaryUV = useSecondaryUV;
        if (_this._lightMap)
            _this.iAddTexture(_this._lightMap);
        return _this;
    }
    Object.defineProperty(EffectLightMapMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return EffectLightMapMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectLightMapMethod.prototype, "blendMode", {
        /**
         * The blend mode with which the light map should be applied to the lighting result.
         *
         * @see EffectLightMapMethod.ADD
         * @see EffectLightMapMethod.MULTIPLY
         */
        get: function () {
            return this._blendMode;
        },
        set: function (value) {
            if (this._blendMode == value)
                return;
            if (value != EffectLightMapMethod.ADD && value != EffectLightMapMethod.MULTIPLY)
                throw new Error('Unknown blendmode!');
            this._blendMode = value;
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectLightMapMethod.prototype, "lightMap", {
        /**
         * The lightMap containing the light map.
         */
        get: function () {
            return this._lightMap;
        },
        set: function (value) {
            if (this._lightMap == value)
                return;
            if (this._lightMap)
                this.iRemoveTexture(this._lightMap);
            this._lightMap = value;
            if (this._lightMap)
                this.iAddTexture(this._lightMap);
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectLightMapMethod.prototype, "useSecondaryUV", {
        /**
         * Indicates whether the secondary UV set should be used to map the light map.
         */
        get: function () {
            return this._useSecondaryUV;
        },
        set: function (value) {
            if (this._useSecondaryUV == value)
                return;
            this._useSecondaryUV = value;
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Indicates the light map should be multiplied with the calculated shading result.
     */
    EffectLightMapMethod.MULTIPLY = 'multiply';
    /**
     * Indicates the light map should be added into the calculated shading result.
     */
    EffectLightMapMethod.ADD = 'add';
    EffectLightMapMethod.assetType = '[asset EffectLightMapMethod]';
    return EffectLightMapMethod;
}(MethodBase));
export { EffectLightMapMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_EffectLightMapMethod provides a method that allows applying a light map texture to the calculated pixel colour.
 * It is different from DiffuseLightMapMethod in that the latter only modulates the diffuse shading value rather
 * than the whole pixel colour.
 */
var _Shader_EffectLightMapMethod = /** @class */ (function (_super) {
    __extends(_Shader_EffectLightMapMethod, _super);
    /**
     * Creates a new EffectEnvMapChunk.
     */
    function _Shader_EffectLightMapMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    /**
     * @inheritDoc
     */
    _Shader_EffectLightMapMethod.prototype._initVO = function (chunkVO) {
        this._lightMap = this._method.lightMap.getAbstraction(this._shader);
        this._lightMap._initVO(chunkVO);
        if (this._method.useSecondaryUV)
            this._shader.secondaryUVDependencies++;
        else
            this._shader.uvDependencies++;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectLightMapMethod.prototype._initConstants = function () {
        this._lightMap._initConstants();
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectLightMapMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code;
        var temp = registerCache.getFreeFragmentVectorTemp();
        code = this._lightMap._getFragmentCode(temp, registerCache, sharedRegisters, this._method.useSecondaryUV ? sharedRegisters.secondaryUVVarying : sharedRegisters.uvVarying);
        switch (this._method.blendMode) {
            case EffectLightMapMethod.MULTIPLY:
                code += 'mul ' + targetReg + ', ' + targetReg + ', ' + temp + '\n';
                break;
            case EffectLightMapMethod.ADD:
                code += 'add ' + targetReg + ', ' + targetReg + ', ' + temp + '\n';
                break;
        }
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectLightMapMethod.prototype._activate = function () {
        this._lightMap.activate();
    };
    _Shader_EffectLightMapMethod.prototype._setRenderState = function (renderState) {
        this._lightMap._setRenderState(renderState);
    };
    return _Shader_EffectLightMapMethod;
}(_Shader_MethodBase));
export { _Shader_EffectLightMapMethod };
ShaderBase.registerAbstraction(_Shader_EffectLightMapMethod, EffectLightMapMethod);
